import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { getCategories, getProducts } from '../../Order/Controller/ProductController';
import { getDigitalMenuCategories, getDigitalMenuProducts, getDigitalMenuStoreDetails } from '../Controller/DigitalMenuController';
import { get, post } from '../../../../GlobalModules/Utility/Http';
import { orderCreatePost } from '../../Order/Model/OrderModel';
import { getProductAddonGroups } from '../Controller/RestaurantController';
import { Toast } from 'react-bootstrap';
import Utility from '../../../../GlobalModules/Utility/Utility';


// Import getBillTypeForCategory function
const getBillTypeForCategory = (categoryLabel, productType, beverageType) => {
    // Check if product_type is BEVERAGE and beverage_type is ALCOHOLIC
    const normalizedProductType = (productType || "").trim().toUpperCase();
    const normalizedBeverageType = (beverageType || "").trim().toUpperCase();
    
    if (normalizedProductType === "BEVERAGE" && normalizedBeverageType === "ALCOHOLIC") {
        return "liquor";
    }
    
    // Fallback to category-based checking if product_type/beverage_type not available
    if (!normalizedProductType && categoryLabel) {
        const LIQUOR_CATEGORIES = ['Bar', 'Liquor', 'Beverages', 'Alcohol', 'Beer', 'Wine', 'Spirits'];
        return LIQUOR_CATEGORIES.some(cat => 
            categoryLabel.toLowerCase().includes(cat.toLowerCase())
        ) ? "liquor" : "food";
    }
    
    return "food";
};

// Create context
export const RestaurantMenuContext = createContext();

// Provider component
export const RestaurantMenuProvider = ({ children, menuSlug }) => {
    // State
    const [loading, setLoading] = useState(true);
    const [menuData, setMenuData] = useState(null);
    const [storeDetails, setStoreDetails] = useState(null);
    const [storeId, setStoreId] = useState(localStorage.getItem('store_id') || '');
    const [storeLevelTaxAmount, setStoreLevelTaxAmount] = useState(0);
    const [storeLevelDiscountAmount, setStoreLevelDiscountAmount] = useState(0);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null); // Keep for backward compatibility
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState({});
    const [filteredProducts, setFilteredProducts] = useState({});
    const [keyword, setKeyword] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    // Cart state
    const [cart, setCart] = useState({});
    const [showCart, setShowCart] = useState(false);
    const [cartTotals, setCartTotals] = useState({
        subTotal: 0,
        discountTotal: 0,
        totalAfterDiscount: 0,
        taxTotal: 0,
        total: 0,
        itemCount: 0
    });

    // Order success state
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [orderData, setOrderData] = useState(null);
    
    // Customize modal state - match Order module exactly
    const [showAddonModal, setShowAddonModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [addonGroups, setAddonGroups] = useState([]);
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [addonProductGroupProcessing, setAddonProductGroupProcessing] = useState(false);

    // Variant modal state - match Order module exactly
    const [showVariantModal, setShowVariantModal] = useState(false);
    const [variantProduct, setVariantProduct] = useState(null);
    const [selectedVariantOptions, setSelectedVariantOptions] = useState({});

    // Toast notification state
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    // Table information state
    const [tableInfo, setTableInfo] = useState(null);
    const [tableList, setTableList] = useState([]);
    const [loadingTables, setLoadingTables] = useState(false);
    
    // Order history state
    const [orderHistory, setOrderHistory] = useState([]);
    const [showOrderHistory, setShowOrderHistory] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    
    // Digital menu store ID state
    const [digitalmenu_store_id, setDigitalmenu_store_id] = useState(null);
    
    // Store data state
    const [storeData, setStoreData] = useState(null);

    // Debounce timer ref for search
    const searchTimeoutRef = useRef(null);

    // Extract store slack from URL
    const getStoreSlackFromUrl = useCallback(() => {
        const currentHash = window.location.hash;
        const match = currentHash.match(/our_menu\/([^/]+)/) || currentHash.match(/full_menu\/([^/]+)/);
        return match ? match[1] : null;
    }, []);

    // Check if we should use digital menu (webhook) authentication
    const shouldUseDigitalMenu = useCallback(() => {
        // Check if webhook API key is available
        let webhookApiKey = process.env.WEBHOOK_API_KEY;
        
        // For public menu routes, set default webhook key if not already set
        const currentHash = window.location.hash;
        const currentPath = window.location.pathname;

        const isPublicMenuRoute = currentHash.includes('/our_menu/') || currentHash.includes('/full_menu/');
        const isPublicMenuPath = currentPath.includes('/our_menu/') || currentPath.includes('/full_menu/');
        
        // console.log('🔍 DEBUG: Route Detection:', {
        //     currentHash,
        //     currentPath,
        //     isPublicMenuRoute,
        //     isPublicMenuPath,
        //     webhookApiKeyExists: !!webhookApiKey
        // });
        
        if ((isPublicMenuRoute || isPublicMenuPath) && !webhookApiKey) {
            // Try to get webhook key from URL parameters first
            const urlParams = new URLSearchParams(window.location.search);
            const urlWebhookKey = urlParams.get('webhook_api_key');
            
            if (urlWebhookKey) {
                localStorage.setItem('webhook_api_key', urlWebhookKey);
                webhookApiKey = urlWebhookKey;
                // console.log('🔑 Using webhook key from URL parameter:', urlWebhookKey);
            } else {
                // Set correct webhook key for public access (from backend logs)
                const correctWebhookKey = process.env.WEBHOOK_API_KEY;
                localStorage.setItem('webhook_api_key', correctWebhookKey);
                webhookApiKey = correctWebhookKey;
                // console.log('🔑 Auto-set correct webhook key for public menu access:', correctWebhookKey);
                // console.log('💡 TIP: You can pass a different webhook key via URL: ?webhook_api_key=YOUR_KEY');
            }
        }
        
        // Check if we're in a public menu context (no regular authentication)
        const hasRegularAuth = localStorage.getItem('token') || localStorage.getItem('accessToken');
        
        // Check if webhook mode is forced
        const forceWebhook = localStorage.getItem('force_webhook_auth') === 'true';
        
        // Check current route - handle both hash router and regular router
        const isPublicMenu = isPublicMenuRoute || isPublicMenuPath;
        
        // console.log('🔍 Webhook API Key check:', {
        //     webhookApiKey: webhookApiKey ? 'Present' : 'Not found',
        //     webhookKeyPreview: webhookApiKey ? `${webhookApiKey.substring(0, 10)}...` : 'None',
        //     hasRegularAuth: !!hasRegularAuth,
        //     currentPath: currentPath,
        //     currentHash: currentHash,
        //     isPublicMenu: isPublicMenu,
        //     isPublicMenuRoute: isPublicMenuRoute,
        //     forceWebhook: forceWebhook,
        //     shouldUseWebhook: webhookApiKey && (forceWebhook || !hasRegularAuth || isPublicMenu)
        // });
        
        // Use digital menu if we have webhook key and (forced, no regular auth, or in public menu path)
        return webhookApiKey && (forceWebhook || !hasRegularAuth || isPublicMenu);
    }, []);
    const handleRefresh = useCallback(() => {
        fetchMenuData();
    },);
    // Function to fetch store details using storeSlack and webhook API key
    const fetchStoreDetails = useCallback(async (storeSlack, webhookApiKey) => {
        try {
            // console.log('🏪 Fetching store details for:', storeSlack);
            
            const response = await getDigitalMenuStoreDetails(storeSlack);
            
            if (response && response.success && response.data && response.data.id) {
                const storeId = response.data.id;
                const storeInfo = response.data;
                // console.log("storeinfo====",storeInfo)
                setDigitalmenu_store_id(storeId);
                setStoreData(storeInfo);
                setStoreDetails(storeInfo);
                
                // Store the store ID in localStorage for use in other API calls
                localStorage.setItem('digitalmenu_store_id', storeId.toString());
                
                // Store complete store data in localStorage
                localStorage.setItem('digitalmenu_store_data', JSON.stringify(storeInfo));
                
                // console.log('✅ Store details fetched successfully. Store ID:', storeId);
                // console.log('🏪 Store Name:', storeInfo.name);
                // console.log('💾 Store data saved to localStorage');
                
                // Return true to indicate successful store fetch
                return true;
                
            } else {
                console.error('❌ Invalid store details response:', response);
                return false;
            }
        } catch (error) {
            console.error('❌ Error fetching store details:', error);
            console.error('Store Slack:', storeSlack);
            return false;
        }
    }, []);

    // Utility function to set webhook API key for testing
    const setWebhookApiKey = useCallback((apiKey) => {
        localStorage.setItem('webhook_api_key', apiKey);
        console.log('✅ Webhook API key set:', apiKey);
    }, []);

    // Utility function to reset to correct webhook key
    const resetToCorrectWebhookKey = useCallback(() => {
        const correctWebhookKey = process.env.WEBHOOK_API_KEY;
        localStorage.setItem('webhook_api_key', correctWebhookKey);
        console.log('🔄 Reset to correct webhook API key:', correctWebhookKey);
        // Force page reload to use new key
        window.location.reload();
    }, []);

    // Test function to verify webhook key setup
    const testWebhookKey = useCallback(() => {
        const webhookApiKey = localStorage.getItem('webhook_api_key');
        const forceWebhook = localStorage.getItem('force_webhook_auth');
        
        // console.log('🧪 Webhook Test Results:', {
        //     webhookKeyExists: !!webhookApiKey,
        //     webhookKeyPreview: webhookApiKey ? `${webhookApiKey.substring(0, 10)}...` : 'NOT SET',
        //     forceWebhookMode: forceWebhook === 'true',
        //     shouldUseWebhook: shouldUseDigitalMenu()
        // });
        
        if (!webhookApiKey) {
            console.error('❌ No webhook API key found! Use: setWebhookApiKey("your_key")');
        } else {
            console.log('✅ Webhook key is ready for use');
        }
    }, [shouldUseDigitalMenu]);

    // Utility function to enable webhook mode for testing
    const enableWebhookMode = useCallback((enable = true) => {
        localStorage.setItem('force_webhook_auth', enable.toString());
        console.log('Webhook mode forced:', enable);
    }, []);

    // Utility function to test API connectivity with current webhook key
    const testWebhookApiConnectivity = useCallback(async () => {
        const webhookApiKey = localStorage.getItem('webhook_api_key');
        console.log('🧪 Testing webhook API connectivity...');
        
        if (!webhookApiKey) {
            console.error('❌ No webhook API key found!');
            return false;
        }
        
        try {
            // Test with a simple category API call
            const testFilters = {
                store_id: localStorage.getItem('store_id') || '9',
                status: 1
            };
            
            console.log('🔄 Testing category API with webhook auth...');
            const response = await getDigitalMenuCategories(testFilters);
            
            if (response.success) {
                console.log('✅ Webhook API test successful!', response);
                return true;
            } else {
                console.log('❌ Webhook API test failed:', response.message);
                return false;
            }
        } catch (error) {
            console.log('❌ Webhook API test error:', error);
            return false;
        }
    }, []);

    // Fetch menu data
    const fetchMenuData = useCallback(async () => {
        try {
            setLoading(true);
            // console.log('fetchMenuData called, storeId:', storeId);

            // Priority order for store_id: digitalmenu_store_id > storeId > localStorage store_id
            let currentStoreId = localStorage.getItem('digitalmenu_store_id') || 
                                storeId || 
                                localStorage.getItem('store_id');
            
            // console.log('📊 Store ID Resolution:', {
            //     digitalmenu_store_id: localStorage.getItem('digitalmenu_store_id'),
            //     stateStoreId: storeId,
            //     localStorageStoreId: localStorage.getItem('store_id'),
            //     finalStoreId: currentStoreId
            // });
            
            if (!currentStoreId) {
                console.error('❌ No store_id found in any source');
                // Try to use a default store_id for testing - you should replace this with proper store selection
                // currentStoreId = '9'; // Replace with your actual store ID
                console.warn('⚠️ Using fallback test store_id:', currentStoreId);
                
                // For now, let's continue with test store ID
                // In production, you should redirect to store selection or show an error
                // setLoading(false);
                // return;
            }
            
            // Fetch store information from local storage
            const currentStore = localStorage.getItem('currentStore') ? 
                JSON.parse(localStorage.getItem('currentStore')) : null;
                
            // Initialize menu data structure
            const menuData = {
                store: {
                    name: currentStore?.name || 'Restaurant Menu',
                    logo: currentStore?.logo || '/images/default-logo.png',
                    currency_code: currentStore?.currency_code || 'INR',
                    slack: currentStore?.slack || ''
                },
                table: null,
                categories: [],
                category_products: {}
            };
            
            // Fetch store_id from localStorage
            // const storeId = localStorage.getItem('store_id');
            
            // Prepare filters for category request
            const categoryFilters = {
                store_id: currentStoreId,
                status: 1
            };
            
            // console.log('Category filters:', categoryFilters);
            
            // Determine which controller to use based on authentication context
            const useDigitalMenu = shouldUseDigitalMenu();
            // console.log('Using digital menu authentication:', useDigitalMenu);
            // Fetch categories with filters
            let categoriesResponse;
            try {
                categoriesResponse = useDigitalMenu 
                    ? await getDigitalMenuCategories(categoryFilters)
                    : await getCategories(categoryFilters);
                // console.log('✅ Categories API Response:', categoriesResponse);
            } catch (error) {
                console.error('❌ Categories API Error:', error);
                // console.error('Error details:', {
                //     status: error.response?.status,
                //     message: error.response?.data?.message,
                //     useDigitalMenu: useDigitalMenu,
                //     webhookKey: localStorage.getItem('webhook_api_key') ? 'Present' : 'Missing'
                // });
                throw error; // Re-throw to maintain existing error handling
            }
            if (categoriesResponse.success && categoriesResponse.data) {
                // Map API response to our structure
                const categories = categoriesResponse.data.map(cat => ({
                    label: cat.name || cat.label,
                    slug: cat.slack || cat.id,
                    id: cat.id
                }));
                
                menuData.categories = categories;
                
                // Prepare filters for product request
                const productFilters = {
                    store_id: currentStoreId,
                    status: 1
                };
                
                // console.log('Product filters:', productFilters);
                
                // Fetch products with filters
                const productsResponse = useDigitalMenu 
                    ? await getDigitalMenuProducts(productFilters)
                    : await getProducts(productFilters);
                if (productsResponse.success && productsResponse.data) {
                    // Organize products by category
                    const categoryProducts = {};
                    
                    // Initialize category_products with empty arrays
                    categories.forEach(category => {
                        if (category && category.slug) {
                            categoryProducts[category.slug] = {
                                label: category.label || '',
                                products: []
                            };
                        }
                    });
                    
                    // Fill in products by category
                    productsResponse.data.forEach(product => {
                        if (!product) return;
                        
                        const categorySlack = product.Category?.slack;
                        
                        if (categorySlack && categoryProducts[categorySlack]) {
                            try {
                                // Debug original product data
                                // console.log('=== PRODUCT TAX DEBUG ===');
                                // console.log('Original product:', product);
                                // console.log('TaxCode:', product.TaxCode);
                                // console.log('tax_percentage:', product.tax_percentage);
                                // console.log('is_taxable:', product.is_taxable);
                                
                                // Extract tax information with multiple fallbacks
                                let taxPercentage = 0;
                                let isTaxable = false;
                                
                                // Try different ways to get tax percentage
                                if (product.TaxCode?.total_tax_percentage) {
                                    taxPercentage = parseFloat(product.TaxCode.total_tax_percentage);
                                } else if (product.tax_percentage) {
                                    taxPercentage = parseFloat(product.tax_percentage);
                                } else if (product.TaxCode?.tax_percentage) {
                                    taxPercentage = parseFloat(product.TaxCode.tax_percentage);
                                } else {
                                    // If no tax found, set a default tax for testing (you can remove this later)
                                    taxPercentage = 18; // Default 18% GST for testing
                                    // console.warn(`No tax found for product ${product.name}, using default 18%`);
                                }
                                
                                // Try different ways to get is_taxable
                                if (product.is_taxable === 1 || product.is_taxable === true || product.is_taxable === 'true') {
                                    isTaxable = true;
                                } else if (taxPercentage > 0) {
                                    // If tax percentage exists, assume it's taxable
                                    isTaxable = true;
                                }
                                // console.log('product_discount---',product.DiscountCode)
                                // Format the product data with fallbacks for missing fields
                                const productData = {
                                    id: product.slack || '',
                                    slack: product.slack || '',
                                    name: product.name || 'Unnamed Product',
                                    price: product.sale_amount_excluding_tax || '0.00',
                                    discount: product.DiscountCode?.discount_percentage || 0,
                                    image: product.images?.[0]?.thumbnail || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y4ZjlmYSIvPjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNmM3NTdkIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5NZW51PC90ZXh0Pjwvc3ZnPg==',
                                    tax_percentage: taxPercentage,
                                    is_taxable: isTaxable,
                                    // Handle different variant field names from API
                                    variants: product.variants || product.ProductVariants || product.product_variants || product.Variants || [],
                                    // Include original product data for variant processing
                                    ...product
                                };
                                
                                // Debug final tax information
                                // console.log(`Final Product ${productData.name} - Tax: ${productData.tax_percentage}%, Taxable: ${productData.is_taxable}`);
                                // console.log('=== END PRODUCT DEBUG ===');
                                
                                // Only add products with valid IDs
                                if (productData.id) {
                                    categoryProducts[categorySlack].products.push(productData);
                                }
                            } catch (err) {
                                console.error('Error processing product:', err, product);
                            }
                        }
                    });
                    
                    // Test product temporarily removed to fix compilation
                    
                    // Remove categories with no products
                    Object.keys(categoryProducts).forEach(key => {
                        if (categoryProducts[key].products.length === 0) {
                            delete categoryProducts[key];
                        }
                    });
                    
                    menuData.category_products = categoryProducts;
                }
            }
            
            // If there's a specific menuSlug, try to fetch table info
            if (menuSlug) {
                try {
                    // Prepare filters as required
                    const filters = {
                        store_id: currentStoreId,
                        status: 1
                    };
                    
                    // Create query params with filters
                    const queryParams = {
                        filters: JSON.stringify(filters)
                    };
                    
                    // console.log('Restaurant table filters:', queryParams);
                    
                    // Use the filters in the API call with webhook authentication
                    const response = await get(`${process.env.EXTENDED_URL}restaurant_table`, queryParams, "webhook");
                    
                    if (response.data && response.data.success) {
                        menuData.table = {
                            table_number: response.data.data.table_number || 'Unknown',
                            slack: response.data.data.slack
                        };
                    }
                } catch (error) {
                    console.error('Error fetching table details:', error);
                    // Continue anyway even if table info fails
                }
            }
            
            setMenuData(menuData);
            setCategories(menuData.categories);
            setProducts(menuData.category_products);
            setFilteredProducts(menuData.category_products);
        } catch (error) {
            console.error('Error fetching menu:', error);
        } finally {
            setLoading(false);
        }
    }, [menuSlug, shouldUseDigitalMenu, storeId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch available tables for restaurant
    const fetchAvailableTables = useCallback(async () => {
        try {
            setLoadingTables(true);
            
            // Get store_id from localStorage if not in state
            let currentStoreId = localStorage.getItem('digitalmenu_store_id') || 
                                storeId || 
                                localStorage.getItem('store_id');
            
            if (!currentStoreId) {
                console.warn('No store_id found for table fetch');
                // currentStoreId = '9'; // Fallback for testing
            }
            
            // Prepare filters for table request
            const tableFilters = {
                store_id: currentStoreId,
                status: 1
            };
            
            // Fetch tables with filters (same endpoint as in fetchMenuData)
            const useDigitalMenu = shouldUseDigitalMenu();
            const response = await get(`${process.env.EXTENDED_URL}restaurant_table`, { filters: JSON.stringify(tableFilters) }, useDigitalMenu ? "webhook" : ""); 
            
            if (response.data && response.data.success) {
                // Handle both array and paginated responses
                const tables = Array.isArray(response.data.data) 
                    ? response.data.data 
                    : (response.data.data?.data || []);
                
                const formattedTables = tables.map(table => {
                    // Check if latestOrder exists and has any orders
                    const hasOrders = table.latestOrder && Array.isArray(table.latestOrder) && table.latestOrder.length > 0;
                    
                    return {
                        id: table.id,
                        slack: table.slack,
                        table_number: table.table_number,
                        name: table.name || `${table.table_number}`,
                        isAvailable: !hasOrders, // Available when latestOrder is empty
                        latestOrder: table.latestOrder || [],
                        noOfOccupants: table?.no_of_occupants || ''
                    };
                });
                
                setTableList(formattedTables);
                console.log('✅ Tables fetched successfully:', formattedTables);
            } else {
                console.warn('No tables found or invalid response');
                setTableList([]);
            }
        } catch (error) {
            console.error('Error fetching available tables:', error);
            setTableList([]);
        } finally {
            setLoadingTables(false);
        }
    }, [storeId, shouldUseDigitalMenu]);

    // Fetch products by category (similar to Order module)
    const fetchProductsByCategory = useCallback(async (categoryIds = null) => {
        setLoading(true);
        try {
            // Get store_id from localStorage if not in state
            let currentStoreId = localStorage.getItem('digitalmenu_store_id') || storeId || localStorage.getItem('store_id');
            console.log('fetchProductsByCategory - Using store_id:', currentStoreId);
            
            if (!currentStoreId) {
                console.error('No store_id found for product fetch');
                // Use test store_id for debugging
                // currentStoreId = '9';
                console.warn('fetchProductsByCategory - Using test store_id:', currentStoreId);
            }
            
            // Prepare filters for product request
            const productFilters = {
                store_id: currentStoreId,
                status: 1
            };
            
            // Add category filter if categories are selected
            if (categoryIds) {
                // Support both single ID and array of IDs
                productFilters.category_id = Array.isArray(categoryIds) ? categoryIds : [categoryIds];
            }
            
            console.log('Product filters for category:', productFilters);
            
            // Fetch products with filters
            // const productsResponse = await getProducts(productFilters);
            const productsResponse = await getDigitalMenuProducts(productFilters);

            if (productsResponse.success && productsResponse.data) {
                // Process products similar to fetchMenuData
                const categoryProducts = {};
                
                // Initialize categories if they exist
                if (menuData && menuData.categories) {
                    menuData.categories.forEach(category => {
                        if (category && category.slug) {
                            categoryProducts[category.slug] = {
                                label: category.label || '',
                                products: []
                            };
                        }
                    });
                }
                console.log("productResponse====",productsResponse)
                // Process products
                productsResponse.data.forEach(product => {
                    if (!product) return;
                    
                    const categorySlack = product.Category?.slack;
                    
                    if (categorySlack && categoryProducts[categorySlack]) {
                        try {
                            const productData = {
                                id: product.slack || '',
                                slack: product.slack || '',
                                name: product.name || 'Unnamed Product',
                                price: product.sale_amount_excluding_tax || '0.00',
                                discount: product.discount_code?.discount_percentage || 0,
                                image: product.ProductImages?.[0]?.filename || product.images?.[0]?.thumbnail || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y4ZjlmYSIvPjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNmM3NTdkIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5NZW51PC90ZXh0Pjwvc3ZnPg==',
                                tax_percentage: product.TaxCode?.total_tax_percentage || 0,
                                is_taxable: product.is_taxable || false,
                                variants: product.variants || product.ProductVariants || product.product_variants || product.Variants || [],
                                ...product,
                                product: product
                            };
                            
                            if (productData.id) {
                                categoryProducts[categorySlack].products.push(productData);
                            }
                        } catch (err) {
                            console.error('Error processing product:', err, product);
                        }
                    }
                });
                
                // Update filtered products based on selection
                // Don't filter here - let filterProductsByCategory handle it
                setFilteredProducts(categoryProducts);
                
                // Add a simple test product with variants for testing
                // if (Object.keys(categoryProducts).length > 0) {
                //     const firstCategoryKey = Object.keys(categoryProducts)[0];
                //     const testProduct = {
                //         id: 'test-variant-product',
                //         slack: 'test-variant-product',
                //         name: 'Test Pizza (Has Variants)',
                //         price: '15.99',
                //         discount: 0,
                //         image: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2ZmNjk5NCIvPjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+VGVzdDwvdGV4dD48L3N2Zz4=',
                //         tax_percentage: 0,
                //         is_taxable: false,
                //         variants: [
                //             { VariantOption: { label: 'Size' }, Product: { name: 'Small', product_code: 'S', sale_amount_excluding_tax: 12.99, slack: 'small' } },
                //             { VariantOption: { label: 'Size' }, Product: { name: 'Large', product_code: 'L', sale_amount_excluding_tax: 18.99, slack: 'large' } }
                //         ]
                //     };
                //     categoryProducts[firstCategoryKey].products.unshift(testProduct);
                // }
                
                // Update all products
                setProducts(categoryProducts);
            }
        } catch (error) {
            console.error('Error fetching products by category:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedCategories, storeId, categories]);
    
    // Filter products by category
    const filterProductsByCategory = useCallback(() => {
        // Check if we have selected categories
        if (selectedCategories.length > 0) {
            // Filter products from the products state
            const filtered = {};
            
            // Process each selected category
            selectedCategories.forEach(categorySlug => {
                if (products[categorySlug]) {
                    filtered[categorySlug] = products[categorySlug];
                }
            });
            
            // If we have category IDs, fetch products with those IDs
            const categoryIds = [];
            selectedCategories.forEach(categorySlug => {
                const category = categories.find(cat => cat.slug === categorySlug);
                if (category) {
                    categoryIds.push(category.id);
                }
            });
            
            if (categoryIds.length > 0) {
                fetchProductsByCategory(categoryIds);
            } else {
                setFilteredProducts(filtered);
            }
        } else if (selectedCategory) {
            // For backward compatibility - handle single category selection
            const category = categories.find(cat => cat.slug === selectedCategory);
            if (category) {
                fetchProductsByCategory([category.id]);
            } else {
                // Filter from existing data if category not found
                const filtered = {};
                if (products[selectedCategory]) {
                    filtered[selectedCategory] = products[selectedCategory];
                }
                setFilteredProducts(filtered);
            }
        } else {
            // Show all products when no category is selected
            setFilteredProducts(products);
        }
    }, [selectedCategories, selectedCategory, products, categories, fetchProductsByCategory]);

// Toggle category selection with multi-select support
    const toggleCategory = useCallback((categorySlug) => {
        // For backward compatibility
        if (selectedCategory === categorySlug) {
            setSelectedCategory(null);
        } else {
            setSelectedCategory(categorySlug);
        }
        
        // Handle the array of selected categories
        setSelectedCategories(prev => {
            // Check if this category is already selected
            if (prev.includes(categorySlug)) {
                // Remove it if it's already selected
                return prev.filter(slug => slug !== categorySlug);
            } else {
                // Add it if it's not selected
                return [...prev, categorySlug];
            }
        });
    }, [selectedCategory]);

    // Search functionality with debounce - API calls after 2+ characters and 500ms delay
    const handleSearch = useCallback((searchTerm) => {
        const searchValue = searchTerm.trimStart();
        setKeyword(searchValue);

        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // If less than 2 characters, clear search
        if (searchValue.length < 2) {
            setIsSearching(false);
            setSearchResults([]);
            return;
        }

        // Set debounce timeout for 500ms
        searchTimeoutRef.current = setTimeout(async () => {
            setIsSearching(true);
            
            try {
                // Get store_id from localStorage if not in state
                let currentStoreId = localStorage.getItem('digitalmenu_store_id') || storeId || localStorage.getItem('store_id');
                
                if (!currentStoreId) {
                    console.error('No store_id found for search');
                    // currentStoreId = '9'; // Fallback for testing
                }
                
                // Prepare filters for search API call
                const searchFilters = {
                    // limit: 100,
                    // offset: 0,
                    store_id: currentStoreId,
                    is_ingredient: 0,
                    is_addon_product: 0,
                    search_attr: searchValue,
                    exclude_alcoholic: "true"
                };
                
                const filterOp = {
                    search_attr: "substring"
                };
                
                console.log('Search filters:', searchFilters);
                console.log('Search filter_op:', filterOp);
                
                // Make API call with search parameters
                // const searchResponse = await getProducts(searchFilters, filterOp);
                const searchResponse = await getDigitalMenuProducts(searchFilters);
                if (searchResponse.success && searchResponse.data) {
                    // Process search results similar to regular products
                    const results = searchResponse.data.map(product => {
                        // Extract tax information with multiple fallbacks
                        let taxPercentage = 0;
                        let isTaxable = false;
                        
                        if (product.TaxCode?.total_tax_percentage) {
                            taxPercentage = parseFloat(product.TaxCode.total_tax_percentage);
                        } else if (product.tax_percentage) {
                            taxPercentage = parseFloat(product.tax_percentage);
                        } else if (product.TaxCode?.tax_percentage) {
                            taxPercentage = parseFloat(product.TaxCode.tax_percentage);
                        } else {
                            taxPercentage = 18; // Default 18% GST for testing
                        }
                        
                        if (product.is_taxable === 1 || product.is_taxable === true || product.is_taxable === 'true') {
                            isTaxable = true;
                        } else if (taxPercentage > 0) {
                            isTaxable = true;
                        }
                        
                        return {
                            id: product.slack || '',
                            slack: product.slack || '',
                            name: product.name || 'Unnamed Product',
                            price: product.sale_amount_excluding_tax || '0.00',
                            discount: product.discount_code?.discount_percentage || 0,
                            image: product.images?.[0]?.thumbnail || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y4ZjlmYSIvPjx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNmM3NTdkIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5NZW51PC90ZXh0Pjwvc3ZnPg==',
                            description: product.description || '',
                            category: product.Category?.name || 'Uncategorized',
                            categorySlug: product.Category?.slack || '',
                            tax_percentage: taxPercentage,
                            is_taxable: isTaxable,
                            customizable: product.addon_groups && product.addon_groups.length > 0,
                            has_variant: product.has_variant || false,
                            variants: product.variants || [],
                            TaxCode: product.TaxCode || null,
                            addon_groups: product.addon_groups || [],
                            product: product,
                            is_inventory_manage: product.is_inventory_manage || 0,
                            quantity: product.quantity || 0,
                        };
                    });
                    
                    setSearchResults(results);
                } else {
                    setSearchResults([]);
                }
            } catch (error) {
                console.error('Search API error:', error);
                setSearchResults([]);
            }
        }, 500); // 500ms debounce delay
    }, [storeId]);

    // Helper function to generate consistent cart keys
    const generateCartKey = useCallback((product, variantInfo = null) => {
        const baseKey = product.slack || product.product_slack || product.id;
        if (variantInfo) {
            const variantKey = variantInfo.variant_slack || `variant_${Date.now()}`;
            return `${baseKey}_${variantKey}`;
        }
        return baseKey;
    }, []);

    // Add variant to cart - exact same as Order module
    const addVariantToCart = useCallback((variantProduct) => {
        // console.log('=== ADD VARIANT TO CART DEBUG ===');
        // console.log('Variant product:', variantProduct);
        // console.log('Variant Product.TaxCode:', variantProduct.Product?.TaxCode);
        // console.log('Variant is_taxable:', variantProduct.Product?.is_taxable);
        
        const updatedCart = { ...cart };

        const existingItemKey = Object.keys(updatedCart).find(key => {
            const item = updatedCart[key];
            const sameProduct = item.product_slack === variantProduct.slack;
            const sameVariant = JSON.stringify(item.variant_options || {}) === JSON.stringify(variantProduct.variant_options || {});
            const noAddons = !item.selected_addon_products || Object.keys(item.selected_addon_products).length === 0;
            const variantIsSame = !variantProduct.customizable && sameVariant && noAddons;
            return sameProduct && variantIsSame;
        });

        if (existingItemKey) {
            updatedCart[existingItemKey].quantity += 1;
            setToastMessage(`${variantProduct.name} quantity updated in cart`);
        } else {
            // Generate consistent cart key using helper function
            const itemKey = generateCartKey(variantProduct, { variant_slack: variantProduct.variant_slack || `variant_${Date.now()}` });

            // Get variant option name if available
            const variantOptionName = variantProduct.variant_option_value?.variant_option_value || "";

            // Format the name with variant information if available
            const displayName = variantOptionName ? `${variantProduct.name} (${variantOptionName})` : variantProduct.name;

            // Extract tax information from variant product structure
            let taxPercentage = 0;
            let isTaxable = false;
            
            // For variants, tax info is in variantProduct.Product.TaxCode
            if (variantProduct.Product?.TaxCode?.total_tax_percentage) {
                taxPercentage = parseFloat(variantProduct.Product.TaxCode.total_tax_percentage);
            } else if (variantProduct.TaxCode?.total_tax_percentage) {
                taxPercentage = parseFloat(variantProduct.TaxCode.total_tax_percentage);
            } else if (variantProduct.tax_percentage) {
                taxPercentage = parseFloat(variantProduct.tax_percentage);
            } else {
                // Default tax for testing
                taxPercentage = 18;
                console.warn(`No tax found for variant ${variantProduct.name}, using default 18%`);
            }
            
            // Check is_taxable from Product or variant itself
            if (variantProduct.Product?.is_taxable === 1 || variantProduct.Product?.is_taxable === true) {
                isTaxable = true;
            } else if (variantProduct.is_taxable === 1 || variantProduct.is_taxable === true) {
                isTaxable = true;
            } else if (taxPercentage > 0) {
                isTaxable = true;
            }
            
            // Extract discount information from variant product
            let discountPercentage = 0;
            if (variantProduct.Product?.DiscountCode?.discount_percentage) {
                discountPercentage = parseFloat(variantProduct.Product.DiscountCode.discount_percentage);
            } else if (variantProduct.DiscountCode?.discount_percentage) {
                discountPercentage = parseFloat(variantProduct.DiscountCode.discount_percentage);
            } else if (variantProduct.discount_percentage) {
                discountPercentage = parseFloat(variantProduct.discount_percentage);
            } else if (variantProduct.discount_code?.discount_percentage) {
                discountPercentage = parseFloat(variantProduct.discount_code.discount_percentage);
            } else if (variantProduct.discount) {
                discountPercentage = parseFloat(variantProduct.discount);
            }
            
            // console.log(`Variant tax - Tax%: ${taxPercentage}, Taxable: ${isTaxable}`);
            // console.log(`Variant discount - Discount%: ${discountPercentage}`);

            const cartItem = {
                product_code: variantProduct.product_code || "",
                product_slack: variantProduct.product_slack || variantProduct.slack,
                variant_slack: variantProduct.variant_slack || `variant_${Date.now()}`,
                name: displayName,
                quantity: 1,
                price: variantProduct.sale_amount_excluding_tax || variantProduct.price || 0,
                total_price: variantProduct.sale_amount_excluding_tax || variantProduct.price || 0,
                discount_percentage: discountPercentage,
                discount: discountPercentage, // For backward compatibility
                tax_percentage: taxPercentage,
                is_taxable: isTaxable,
                tax_code: variantProduct.Product?.TaxCode?.tax_code || variantProduct.tax_code || "",
                customizable: Boolean(variantProduct.customizable || variantProduct.parent?.customizable),
                selected_addon_products: {},
                variant_options: variantProduct.variant_options || {},
                product: variantProduct
            };
            
            // console.log('Variant cart item:', cartItem);
            // console.log('=== END VARIANT DEBUG ===');
            
            updatedCart[itemKey] = cartItem;
            setToastMessage(`${variantProduct.name} added to cart`);
        }

        setCart(updatedCart);
        setShowVariantModal(false);
        
        // Show toast notification
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    }, [cart]);

    // Add product to cart - exact same as Order module
    const addToCart = useCallback((product) => {
        console.log("addtocart===",product)
        // console.log('=== ADD TO CART DEBUG ===');
        // console.log('addToCart called with product:', product);
        // console.log('Product tax_percentage:', product.tax_percentage);
        // console.log('Product is_taxable:', product.is_taxable);
        // console.log('Product variants:', product.variants);
        // console.log('Variants length:', product.variants?.length);
        
        if (product.variants && product.variants.length) {
            // console.log('Opening variant modal for product:', product.name);
            setVariantProduct(product);
            setShowVariantModal(true);
            return;
        }

        // Use helper function to generate consistent cart key
        const cartKey = generateCartKey(product);
        
        setCart(prev => {
            const newCart = { ...prev };
            const existingItemKey = Object.keys(newCart).find(
                key => newCart[key].product_slack === product.slack && !newCart[key].variant_slack
            );
            
            if (existingItemKey) {
                newCart[existingItemKey] = {
                    ...newCart[existingItemKey],
                    quantity: newCart[existingItemKey].quantity + 1
                };
                setToastMessage(`${product.name} quantity updated in cart`);
            } else {
                // Extract tax information - handle both regular products and variant products
                let taxPercentage = 0;
                let isTaxable = false;
                
                // Check if this is a variant product (has Product.TaxCode structure)
                if (product.Product?.TaxCode?.total_tax_percentage) {
                    taxPercentage = parseFloat(product.Product.TaxCode.total_tax_percentage);
                    isTaxable = product.Product.is_taxable === 1 || product.Product.is_taxable === true;
                } else if (product.TaxCode?.total_tax_percentage) {
                    // Regular product structure
                    taxPercentage = parseFloat(product.TaxCode.total_tax_percentage);
                    isTaxable = product.is_taxable === 1 || product.is_taxable === true;
                } else if (product.tax_percentage) {
                    taxPercentage = parseFloat(product.tax_percentage);
                    isTaxable = product.is_taxable === 1 || product.is_taxable === true;
                } else {
                    // Default tax for testing
                    taxPercentage = 18;
                    isTaxable = true;
                    console.warn(`No tax found for product ${product.name}, using default 18%`);
                }
                
                // If tax percentage exists, assume it's taxable
                if (taxPercentage > 0 && !isTaxable) {
                    isTaxable = true;
                }
                
                // Extract discount information from DiscountCode
                let discountPercentage = 0;
                if (product.DiscountCode?.discount_percentage) {
                    discountPercentage = parseFloat(product.DiscountCode.discount_percentage);
                } else if (product.discount_percentage) {
                    discountPercentage = parseFloat(product.discount_percentage);
                } else if (product.discount) {
                    discountPercentage = parseFloat(product.discount);
                }
                
                // console.log(`Product tax extraction - Tax%: ${taxPercentage}, Taxable: ${isTaxable}`);
                // console.log(`Product discount extraction - Discount%: ${discountPercentage}`);

                const cartItem = {
                    ...product,
                    quantity: 1,
                    product_slack: product.slack || product.id,
                    selected_addon_products: {},
                    // Ensure tax fields are properly set with extracted values
                    tax_percentage: taxPercentage,
                    is_taxable: isTaxable,
                    // Ensure discount fields are properly set
                    discount_percentage: discountPercentage,
                    discount: discountPercentage // For backward compatibility
                };
                
                console.log('Cart item being added:', cartItem);
                // console.log('Cart item tax_percentage:', cartItem.tax_percentage);
                // console.log('Cart item is_taxable:', cartItem.is_taxable);
                
                newCart[cartKey] = cartItem;
                setToastMessage(`${product.name} added to cart`);
            }
            
            // Show toast notification
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
            
            return newCart;
        });
    }, [cart]);

    // Update quantity in cart
    const updateQuantity = useCallback((productId, change) => {
        setCart(prev => {
            const newCart = { ...prev };
            
            if (newCart[productId]) {
                const newQuantity = newCart[productId].quantity + change;
                
                if (newQuantity <= 0) {
                    delete newCart[productId];
                } else {
                    newCart[productId] = {
                        ...newCart[productId],
                        quantity: newQuantity
                    };
                }
            }
            
            return newCart;
        });
    }, []);

    // Remove product from cart
    const removeFromCart = useCallback((productId) => {
        console.log('Removing product from cart:', productId);
        setCart(prev => {
            const newCart = { ...prev };
            delete newCart[productId];
            return newCart;
        });
    }, []);

    // Update cart totals - include addon prices and store-level calculations
    const updateCartTotals = useCallback(() => {
        let subTotal = 0;
        let productLevelDiscountTotal = 0;
        let productLevelTaxTotal = 0;
        let itemCount = 0;
        
        // console.log('Updating cart totals for cart:', cart);
        
        Object.values(cart).forEach(item => {
            // console.log('=== TAX CALCULATION DEBUG ===');
            // console.log('Full item object:', item);
            
            const quantity = item.quantity || 1;
            const price = parseFloat(item.price) || 0;
            const addonPrice = parseFloat(item.addon_price) || 0; // Include addon price
            const discount = parseFloat(item.discount_percentage || item.discount) || 0;
            const taxPercentage = parseFloat(item.tax_percentage) || 0;
            const isTaxable = item.is_taxable === 1 || item.is_taxable === true || item.is_taxable === 'true';
            
            // console.log('Raw tax_percentage:', item.tax_percentage);
            // console.log('Parsed tax_percentage:', taxPercentage);
            // console.log('Raw is_taxable:', item.is_taxable);
            // console.log('Parsed is_taxable:', isTaxable);
            
            // Calculate item subtotal including addons
            const itemSubTotal = (price + addonPrice) * quantity;
            const itemDiscount = (itemSubTotal * discount) / 100;
            const itemAfterDiscount = itemSubTotal - itemDiscount;
            const itemTax = isTaxable ? (itemAfterDiscount * taxPercentage) / 100 : 0;
            
            // console.log(`Item: ${item.name}`);
            // console.log(`Price: ${price}, Quantity: ${quantity}`);
            // console.log(`Tax%: ${taxPercentage}, Taxable: ${isTaxable}`);
            // console.log(`Item After Discount: ${itemAfterDiscount}`);
            // console.log(`Tax Amount: ${itemTax}`);
            // console.log('=== END DEBUG ===');
            
            subTotal += itemSubTotal;
            productLevelDiscountTotal += itemDiscount;
            productLevelTaxTotal += itemTax;
            itemCount += quantity;
        });
        
        // Calculate store-level discount and tax based on store configuration
        let calculatedStoreLevelDiscount = 0;
        let calculatedStoreLevelTax = 0;
        
        if (storeData) {
            // Store-level discount calculation (applied on subtotal)
            const storeDiscountPercentage = storeData?.discount_code?.discount_percentage || 0;
            calculatedStoreLevelDiscount = (subTotal * parseFloat(storeDiscountPercentage)) / 100;
            
            // Store-level tax calculation (applied after all discounts)
            const amountAfterAllDiscounts = subTotal - productLevelDiscountTotal - calculatedStoreLevelDiscount;
            const storeTaxPercentage = storeData?.tax_code?.total_tax_percentage || 0;
            calculatedStoreLevelTax = (amountAfterAllDiscounts * parseFloat(storeTaxPercentage)) / 100;
        }
        
        // Update store-level amounts in state
        setStoreLevelDiscountAmount(calculatedStoreLevelDiscount);
        setStoreLevelTaxAmount(calculatedStoreLevelTax);
        
        // Calculate final totals
        const totalDiscountAmount = productLevelDiscountTotal + calculatedStoreLevelDiscount;
        const totalTaxAmount = productLevelTaxTotal + calculatedStoreLevelTax;
        const totalAfterDiscount = subTotal - totalDiscountAmount;
        const finalTotal = totalAfterDiscount + totalTaxAmount;
        
        // console.log(`Cart Totals - SubTotal: ${subTotal}, Product Tax: ${productLevelTaxTotal}, Store Tax: ${calculatedStoreLevelTax}, Total Tax: ${totalTaxAmount}, Final Total: ${finalTotal}`);
        
        setCartTotals({
            subTotal: subTotal.toFixed(2),
            discountTotal: totalDiscountAmount.toFixed(2),
            totalAfterDiscount: totalAfterDiscount.toFixed(2),
            taxTotal: totalTaxAmount.toFixed(2),
            total: finalTotal.toFixed(2),
            itemCount
        });
    }, [cart, storeData]);

    // Handle customize functionality - exact same as Order module
    const handleCustomize = useCallback((product) => {
        setSelectedProduct(product);
        setShowAddonModal(true);
    }, []);

    // Fetch addon groups for selected product - exact same as Order module
    const fetchAddonGroups = useCallback(async (productSlack) => {
        setAddonProductGroupProcessing(true);
        try {
            const response = await getProductAddonGroups(productSlack);
            if (response.success) {
                setAddonGroups(response.data);
            } else {
                console.error('Failed to fetch addon groups:', response.message);
                setAddonGroups([]);
            }
        } catch (error) {
            console.error('Error fetching addon groups:', error);
            setAddonGroups([]);
        } finally {
            setAddonProductGroupProcessing(false);
        }
    }, []);

    // Fetch addon groups when selected product changes - exact same as Order module
    useEffect(() => {
        if (selectedProduct) {
            fetchAddonGroups(
                selectedProduct.slack || selectedProduct.product_slack || selectedProduct.id
            );
        }
    }, [selectedProduct, fetchAddonGroups]);
    
    // Handle adding addons to product - exact same as Order module
    const handleAddAddonsToProduct = useCallback(() => {
        if (!selectedProduct) return;

        // Create a copy of the current cart
        const updatedCart = { ...cart };

        // Get the product slack - handle both direct slack and product_slack options
        const productSlack = selectedProduct.slack || selectedProduct.product_slack || selectedProduct.id;
        
        // Find the product in the cart
        const productInCart = Object.keys(updatedCart).find(key => {
            const item = updatedCart[key];
            return item.id === productSlack || item.slack === productSlack || item.product_slack === productSlack;
        });

        if (!productInCart) {
            console.error("Product not found in cart");
            setToastMessage("Product not found in cart");
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
            return;
        }

        const product = updatedCart[productInCart];

        // Initialize the selected_addon_products object if it doesn't exist
        if (!product.selected_addon_products) {
            product.selected_addon_products = {};
        }

        // Clear previous selections
        product.selected_addon_products = {};

        // Add current selections
        let addonTotalPrice = 0;

        // Go through all addon groups and find selected products
        addonGroups.forEach(group => {
            group.products.forEach(addon => {
                if (selectedAddons.includes(addon.slack)) {
                    console.log(addon);

                    // Add the addon to the selected products
                    product.selected_addon_products[addon.slack] = {
                        product_slack: addon.slack,
                        product_code: addon.product_code,
                        name: addon.name,
                        price: parseFloat(addon.sale_amount_excluding_tax),
                        quantity: product.quantity, // Match addon quantity with parent product quantity
                        total_discount: 0,
                        total_tax: 0
                    };

                    // Add to the total addon price
                    // Multiply addon price by the product quantity
                    addonTotalPrice += parseFloat(addon.sale_amount_excluding_tax) * product.quantity;
                }
            });
        });

        // Update the addon price in the product
        product.addon_price = addonTotalPrice;
        
        // Recalculate the total price
        product.total_price = parseFloat(product.price) * product.quantity + addonTotalPrice;

        // Update the cart
        setCart(updatedCart);
        
        // Close the modal
        setShowAddonModal(false);
        
        // Trigger cart calculations update
        updateCartTotals();

        // Show success message
        setToastMessage(`Customization saved for ${selectedProduct?.name || 'product'}`);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    }, [selectedProduct, cart, addonGroups, selectedAddons, updateCartTotals]);

    // New order function - reset to initial state
    const newOrder = useCallback(() => {
        setOrderSuccess(false);
        setOrderData(null);
        setCart({});
        setShowCart(false);
        setSelectedCategory(null);
        setKeyword('');
        setIsSearching(false);
        setSearchResults([]);
    }, []);

    // Removed corrupted formatOrderProducts function

    // Submit order
    const submitOrder = useCallback(async (customerData) => {
        if (Object.keys(cart).length === 0) {
            alert('Your cart is empty');
            return;
        }

        if (!customerData.name || !customerData.phone) {
            alert('Please provide your name and contact number');
            return;
        }

        // Ensure store data is available
        if (!menuData) {
            alert('Menu data is not loaded yet. Please wait a moment and try again.');
            return;
        }
        
        try {
            // Get store_id from state or localStorage
            const currentStoreId = localStorage.getItem('digitalmenu_store_id') || storeId || localStorage.getItem('store_id');
            // console.log("currentStoreId ===",currentStoreId);
            // console.log("digital store menu id ===", localStorage.getItem('digitalmenu_store_id'));
            if (!currentStoreId) {
                throw new Error('Store information is missing');
            }
            
            // Group cart items by bill type (food vs liquor)
            const cartItems = Object.values(cart);
            const groupedItems = {
                food: [],
                liquor: []
            };
            
            cartItems.forEach(item => {
                const billType = getBillTypeForCategory(
                    item.Category?.label || item.category || '', 
                    item.product_type, 
                    item.beverage_type
                );
                groupedItems[billType].push(item);
            });
            
            console.log('Grouped cart items by bill type:', groupedItems);
            
            // Create orders for each bill type that has items
            const orderPromises = [];
            const orderTicketTypes = [];
            const orderResults = [];
            
            for (const [billType, items] of Object.entries(groupedItems)) {
                if (items.length === 0) continue;
                
                const orderTicketType = billType === 'liquor' ? 'BOT' : 'KOT';
                orderTicketTypes.push(orderTicketType);
                
                // Prepare order data for this bill type
                const orderData = {
                order_type: customerData.orderType === 'DINEIN' ? 1 : customerData.orderType === 'TAKEAWAY' ? 2 : 3, // 1=DINEIN, 2=TAKEAWAY, 3=DELIVERY
                order_status: 'CUSTOMER_ORDER',
                customer_phone: customerData.phone || '',
                customer_email: customerData.email || '',
                customer_name: customerData.name || '',
                contact_number: customerData.phone || '',
                address: customerData.address || '',
                waiter_id: null,
                table_id: customerData.selectedTableId || tableInfo?.id || null,
                // table_id: tableInfo?.id || null,
                store_id: currentStoreId,
                userId: parseInt(localStorage.getItem('user_id')) || 0,
                orgId: parseInt(localStorage.getItem('org_id')) || 0,
                payment_method: null,
                business_account_id: null,
                restaurant_mode: 1,
                billing_type: 1, // 1=Food, 2=Liquor
                status: 1, // Active status
                additional_discount_percentage: 0,
                order_products: items.map(item => ({
                    product_slack: item.product_slack || item.id || item.slack,
                    // variant_slack: item.variant_slack || null,
                    quantity: parseFloat(item.quantity || 1),
                    // price: parseFloat(item.price || 0),
                    unit_price: parseFloat(item.price || 0),
                    tax_percentage: item.tax_percentage ? parseFloat(item.tax_percentage) : null,
                    discount_percentage: item.discount_percentage ? parseFloat(item.discount_percentage) : null,
                    // Include addon products if any
                    addon_products: item.selected_addon_products ? Object.values(item.selected_addon_products).map(addon => ({
                        product_slack: addon.product_slack,
                        quantity: parseFloat(addon.quantity || 1),
                        unit_price: parseFloat(addon.price || 0),
                        discount_percentage: addon.discount_percentage ? parseFloat(addon.discount_percentage) : null,
                        tax_percentage: addon.tax_percentage ? parseFloat(addon.tax_percentage) : null
                    })) : null
                }))
            };
            // console.log("orderData ===",orderData)
            // Prepare the API data according to backend validation schema (same as OrderController)
            const apiData = {
                // Required parameters
                order_status: orderData.order_status || 'CUSTOMER_ORDER',
                userId: parseInt(orderData.userId) || parseInt(localStorage.getItem('user_id')) || 0,
                orgId: parseInt(orderData.orgId) || parseInt(localStorage.getItem('org_id')) || 0,
                storeId: orderData.store_id ? parseInt(orderData.store_id) : null,
                order_type: parseInt(orderData.order_type) || 1,
                billing_type: parseInt(orderData.billing_type) || 1,
                status: parseInt(orderData.status) || 1,
                order_ticket_type: orderTicketType, // Add order_ticket_type to payload

                // Customer details
                customer_phone: orderData.customer_phone || null,
                customer_email: orderData.customer_email || null,
                contact_number: orderData.contact_number || null,
                address: orderData.address || null,

                // Optional parameters
                payment_method: orderData.payment_method || null,
                cash_amount: orderData.cash_amount ? parseFloat(orderData.cash_amount) : null,
                credit_amount: orderData.credit_amount ? parseFloat(orderData.credit_amount) : null,
                digital_payment_amount: orderData.digital_payment_amount ? parseFloat(orderData.digital_payment_amount) : null,
                business_account_id: orderData.business_account_id || null,
                restaurant_mode: orderData.restaurant_mode || 0,
                additional_discount_percentage: orderData.additional_discount_percentage ? parseFloat(orderData.additional_discount_percentage) : null,
                notes: orderData.notes || null,
                kitchen_status: orderData.kitchen_status ? parseInt(orderData.kitchen_status) : null,
                table_id: orderData.table_id ? parseInt(orderData.table_id) : null,
                waiter_id: orderData.waiter_id ? parseInt(orderData.waiter_id) : null
            };

            if (orderData.customer_id > 0) {
                apiData.customer_name = orderData.customer_name || "";
                apiData.customer_id = parseInt(orderData.customer_id);
            } else {
                apiData.customer_name = orderData.customer_name || "";
            }

                // Order products are already formatted in orderData
                apiData.order_products = orderData.order_products;
                
                console.log(`Creating ${orderTicketType} order with ${items.length} items:`, apiData);
                
                // Use the same API call as OrderController with webhook authentication
                const ORDER_BASE_URL = `${process.env.EXTENDED_URL}order`;
                const response = await post(ORDER_BASE_URL + '/create', apiData, "webhook");
                const result = orderCreatePost(response);
                
                if (result.success) {
                    orderResults.push(result.data);
                    
                    // Store order number in localStorage for history
                    if (result.data && result.data.order_number) {
                        const existingOrderNumbers = localStorage.getItem('restaurant_order_history') || '';
                        const orderNumbers = existingOrderNumbers ? existingOrderNumbers.split(',') : [];
                        
                        // Add new order number if not already exists
                        if (!orderNumbers.includes(result.data.order_number)) {
                            orderNumbers.unshift(result.data.order_number);
                            
                            // Keep only last 50 orders to prevent localStorage bloat
                            if (orderNumbers.length > 50) {
                                orderNumbers.splice(50);
                            }
                            
                            localStorage.setItem('restaurant_order_history', orderNumbers.join(','));
                        }
                    }
                } else {
                    throw new Error(result.message || 'Failed to create order');
                }
            }
            
            // All orders created successfully
            console.log('All orders created successfully:', orderResults);
            
            // Show success message
            const ticketTypesText = orderTicketTypes.join(' & ');
            const orderNumbersText = orderResults.map(o => o.order_number).join(', ');
            // alert(`Order(s) created successfully!\n${ticketTypesText} order(s): ${orderNumbersText}`);
            Utility.toastNotifications(`Order created successfully!\n${ticketTypesText} order(s): ${orderNumbersText}`, "Success", "success");

            
            // Clear cart and show success
            setCart({});
            setOrderSuccess(true);
            setShowCart(false);
            setOrderData(orderResults[0]); // Set first order data for display
        } catch (error) {
            console.error('Error submitting order:', error);
            // alert(`Failed to submit order: ${error.message || 'Please try again.'}`);
        }
    }, [storeId, cart, menuData]);
    
    // Fetch order history
    const fetchOrderHistory = useCallback(async () => {
        try {
            setLoadingHistory(true);
            
            // Get stored order numbers from localStorage
            const storedOrderNumbers = localStorage.getItem('restaurant_order_history');
            if (!storedOrderNumbers) {
                setOrderHistory([]);
                setLoadingHistory(false);
                return;
            }
            
            const orderNumbers = storedOrderNumbers.split(',').filter(num => num.trim());
            if (orderNumbers.length === 0) {
                setOrderHistory([]);
                setLoadingHistory(false);
                return;
            }
            
            // Get store_id
            const currentStoreId = localStorage.getItem('digitalmenu_store_id') || storeId || localStorage.getItem('store_id');
            if (!currentStoreId) {
                console.error('No store_id found for order history');
                setLoadingHistory(false);
                return;
            }
            
            // Prepare API call with filters
            const filters = {
                search_attr: orderNumbers.join(','),
                sort: [{"colId": "id", "sort": "desc"}],
                store_id: parseInt(currentStoreId),
                show_products: true // Include order products in response
            };
            
            const filterOp = {
                search_attr: "substring"
            };
            
            const queryParams = new URLSearchParams({
                filter_op: JSON.stringify(filterOp),
                filters: JSON.stringify(filters)
            });
            
            // console.log('Fetching order history with params:', queryParams.toString());
            
            // Call order list API with webhook authentication
            const ORDER_LIST_URL = `${process.env.EXTENDED_URL}order/list`;
            const response = await get(`${ORDER_LIST_URL}?${queryParams.toString()}`, {}, "webhook");
            
            if (response.data && response.data.success) {
                setOrderHistory(response.data.data || []);
                // console.log('Order history fetched:', response.data.data?.length || 0, 'orders');
            } else {
                console.error('Failed to fetch order history:', response.data?.message);
                setOrderHistory([]);
            }
        } catch (error) {
            console.error('Error fetching order history:', error);
            setOrderHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    }, [storeId]);
    
    // Initialize the menu - handle store setup then fetch data
    const initializeMenu = useCallback(async () => {
        try {
            setLoading(true);
            // console.log('🚀 Starting menu initialization...');
            
            // Step 1: Get store slack from URL
            const storeSlack = getStoreSlackFromUrl();
            // console.log('📍 Store Slack from URL:', storeSlack);
            
            // Step 2: Check if we should use digital menu
            const useDigitalMenu = shouldUseDigitalMenu();
            // console.log('🔐 Use Digital Menu:', useDigitalMenu);
            
            if (useDigitalMenu && storeSlack) {
                // Step 3: Get webhook API key
                let webhookApiKey = localStorage.getItem('webhook_api_key') || process.env.WEBHOOK_API_KEY;
                // console.log("webhook-api ===",process.env.WEBHOOK_API_KEY)
                // console.log('🔑 Webhook API Key available:', !!webhookApiKey);
                
                if (webhookApiKey) {
                    // console.log('🏪 Step 1: Fetching store details for:', storeSlack);
                    
                    // Step 4: Wait for store details to be fetched FIRST
                    const storeDetailsFetched = await fetchStoreDetails(storeSlack, webhookApiKey);
                    
                    if (storeDetailsFetched) {
                        // console.log('✅ Step 2: Store details fetched successfully');
                        // console.log('📊 Step 3: Now fetching categories and products...');
                        
                        // Step 5: Now fetch menu data with the store ID available
                        await fetchMenuData();
                        // console.log('✅ Menu initialization completed successfully');
                    } else {
                        console.error('❌ Failed to fetch store details');
                        setLoading(false);
                    }
                } else {
                    console.error('❌ Missing webhook API key for digital menu');
                    setLoading(false);
                }
            } else if (!useDigitalMenu) {
                console.log('🔓 Using regular authenticated menu');
                // Regular authenticated menu
                await fetchMenuData();
            } else {
                console.error('❌ Missing store slack from URL for digital menu');
                setLoading(false);
            }
        } catch (error) {
            console.error('❌ Error initializing menu:', error);
            setLoading(false);
        }
    }, [getStoreSlackFromUrl, shouldUseDigitalMenu, fetchStoreDetails, fetchMenuData]);

    // Initialize menu when component mounts
    useEffect(() => {
        initializeMenu();
    }, [initializeMenu]);
    
    // Filter products when selectedCategories changes (removed redundant fetchProductsByCategory call)
    useEffect(() => {
        if (menuData && (selectedCategories.length > 0 || selectedCategory)) {
            filterProductsByCategory();
        }
    }, [selectedCategories, selectedCategory, menuData]);
    
    // Update cart totals whenever cart changes
    useEffect(() => {
        updateCartTotals();
    }, [cart]);

    // Fetch available tables when menu data loads
    useEffect(() => {
        if (menuData && storeId) {
            fetchAvailableTables();
        }
    }, [menuData, storeId, fetchAvailableTables]);

    // Extract table information from URL parameters
    /*useEffect(() => {
        console.log('=== TABLE URL DEBUG ===');
        console.log('Full URL:', window.location.href);
        console.log('URL hash:', window.location.hash);
        console.log('URL search:', window.location.search);
        
        const urlParams = new URLSearchParams(window.location.search);
        console.log("URLSearchParams from search:", urlParams);
        console.log("URLSearchParams size:", urlParams.size);
        
        // Also check if parameters are in the hash (for React Router)
        const hashParams = new URLSearchParams(window.location.hash.split('?')[1]);
        console.log("URLSearchParams from hash:", hashParams);
        console.log("Hash params size:", hashParams.size);

        // Try to get parameters from both search and hash
        let tableId = urlParams.get('table_id') || hashParams.get('table_id');
        let tableName = urlParams.get('table_number') || hashParams.get('table_number');
        
        console.log('Extracted tableId:', tableId);
        console.log('Extracted tableName:', tableName);

        if (tableId && tableName) {
            setTableInfo({
                id: parseInt(tableId),
                table_number: tableName
            });
            console.log('Table info extracted from URL:', { id: tableId, table_number: tableName });
        } else {
            console.log('No table parameters found in URL');
        }
        console.log('=== END TABLE URL DEBUG ===');
    }, []);*/


    useEffect(() => {
    // console.log('=== TABLE URL DEBUG ===');
    // console.log('Full URL:', window.location.href);
    // console.log('URL hash:', window.location.hash);
    // console.log('URL search:', window.location.search);
    
    const urlParams = new URLSearchParams(window.location.search);
    // console.log("URLSearchParams from search:", urlParams);
    // console.log("URLSearchParams size:", urlParams.size);
    
    // Check if there is anything after the last "?" in the hash
    const hashQuery = window.location.hash.split('?').pop(); // Get everything after the last "?"
    const hashParams = new URLSearchParams(hashQuery);
    // console.log("URLSearchParams from hash:", hashParams);
    // console.log("Hash params size:", hashParams.size);

    // Try to get parameters from both search and hash
    let tableId = urlParams.get('table_id') || hashParams.get('table_id');
    let tableName = urlParams.get('table_number') || hashParams.get('table_number');
    
    // console.log('Extracted tableId:', tableId);
    // console.log('Extracted tableName:', tableName);

    if (tableId && tableName) {
        setTableInfo({
            id: parseInt(tableId),
            table_number: tableName
        });
        console.log('Table info extracted from URL:', { id: tableId, table_number: tableName });
    } else {
        console.log('No table parameters found in URL');
    }
    // console.log('=== END TABLE URL DEBUG ===');
}, []);

    // Cleanup search timeout on component unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const contextValue = {
        loading,
        menuData,
        categories,
        selectedCategory,
        selectedCategories,
        products,
        filteredProducts,
        keyword,
        searchResults,
        isSearching,
        cart,
        showCart,
        cartTotals,
        orderSuccess,
        orderData,
        tableInfo,
        tableList,
        loadingTables,
        orderHistory,
        showOrderHistory,
        loadingHistory,
        digitalmenu_store_id,
        storeData,
        
        // Customize modal states - match Order module naming
        showAddonGroupModal: showAddonModal,
        setShowAddonGroupModal: setShowAddonModal,
        addonProductGroupProcessing,
        selectedProduct,
        setSelectedProduct,
        addonGroups,
        setAddonGroups,
        selectedAddonSlacks: selectedAddons,
        setSelectedAddonSlacks: setSelectedAddons,
        
        // Variant modal states - match Order module naming
        showVariantModal,
        setShowVariantModal,
        variantProduct,
        setVariantProduct,
        selectedVariantOptions,
        setSelectedVariantOptions,
        
        // Functions
        setSelectedCategory,
        setSelectedCategories,
        toggleCategory,
        handleSearch,
        addToCart,
        addVariantToCart,
        updateQuantity,
        removeFromCart,
        handleCustomize,
        fetchAddonGroups,
        handleAddAddonsToProduct,
        setShowCart,
        submitOrder,
        newOrder,
        fetchProductsByCategory,
        fetchAvailableTables,
        fetchOrderHistory,
        setShowOrderHistory,
        fetchStoreDetails,
        setWebhookApiKey,
        enableWebhookMode,
        testWebhookKey,
        testWebhookApiConnectivity,
        resetToCorrectWebhookKey,
        // Store-level amounts for UI display
        storeLevelTaxAmount,
        storeLevelDiscountAmount
    };

    return (
        <RestaurantMenuContext.Provider value={contextValue}>
            {children}
            
            {/* Toast Notification */}
            <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1050 }}>
                <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide>
                    <Toast.Header>
                        <i className="fas fa-check-circle text-success me-2"></i>
                        <strong className="me-auto">Success</strong>
                    </Toast.Header>
                    <Toast.Body>{toastMessage}</Toast.Body>
                </Toast>
            </div>
        </RestaurantMenuContext.Provider>
    );
};
